import { Mp3Encoder } from "@breezystack/lamejs";

// All clip export is downmixed to MONO (voice podcast) for small files + simple encoding.

export async function decodeAudioFromUrl(url) {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error("Could not load audio");
  const arr = await resp.arrayBuffer();
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  try {
    const audioBuffer = await ctx.decodeAudioData(arr);
    return audioBuffer;
  } finally {
    ctx.close();
  }
}

export function toMono(audioBuffer) {
  const n = audioBuffer.length;
  const ch = audioBuffer.numberOfChannels;
  const out = new Float32Array(n);
  for (let c = 0; c < ch; c++) {
    const d = audioBuffer.getChannelData(c);
    for (let i = 0; i < n; i++) out[i] += d[i] / ch;
  }
  return { samples: out, sampleRate: audioBuffer.sampleRate };
}

export function sliceSamples(samples, sampleRate, startSec, endSec) {
  const start = Math.max(0, Math.floor(startSec * sampleRate));
  const end = Math.min(samples.length, Math.floor(endSec * sampleRate));
  return samples.subarray(start, Math.max(start, end));
}

// segments: [{start, end}] in seconds. Concatenates only those ranges (speaker isolation).
export function concatSegments(samples, sampleRate, segments) {
  const parts = segments
    .map((s) => sliceSamples(samples, sampleRate, s.start, s.end))
    .filter((p) => p.length > 0);
  const total = parts.reduce((sum, p) => sum + p.length, 0);
  const out = new Float32Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}

function clampInt16(samples) {
  const int16 = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return int16;
}

export function encodeWav(samples, sampleRate) {
  const dataSize = samples.length * 2;
  const buf = new ArrayBuffer(44 + dataSize);
  const v = new DataView(buf);
  let o = 0;
  const ws = (s) => { for (let i = 0; i < s.length; i++) v.setUint8(o++, s.charCodeAt(i)); };
  ws("RIFF"); v.setUint32(o, 36 + dataSize, true); o += 4; ws("WAVE"); ws("fmt ");
  v.setUint32(o, 16, true); o += 4;
  v.setUint16(o, 1, true); o += 2;          // PCM
  v.setUint16(o, 1, true); o += 2;          // mono
  v.setUint32(o, sampleRate, true); o += 4;
  v.setUint32(o, sampleRate * 2, true); o += 4;
  v.setUint16(o, 2, true); o += 2;          // block align
  v.setUint16(o, 16, true); o += 2;         // bits
  ws("data"); v.setUint32(o, dataSize, true); o += 4;
  const int16 = clampInt16(samples);
  for (let i = 0; i < int16.length; i++) { v.setInt16(o, int16[i], true); o += 2; }
  return new Blob([buf], { type: "audio/wav" });
}

export function encodeMp3(samples, sampleRate, kbps = 128) {
  const enc = new Mp3Encoder(1, sampleRate, kbps);
  const int16 = clampInt16(samples);
  const blockSize = 1152;
  const data = [];
  for (let i = 0; i < int16.length; i += blockSize) {
    const chunk = int16.subarray(i, i + blockSize);
    const mp3buf = enc.encodeBuffer(chunk);
    if (mp3buf.length > 0) data.push(new Uint8Array(mp3buf));
  }
  const end = enc.flush();
  if (end.length > 0) data.push(new Uint8Array(end));
  return new Blob(data, { type: "audio/mpeg" });
}

export function exportClip(samples, sampleRate, format) {
  return format === "mp3" ? encodeMp3(samples, sampleRate) : encodeWav(samples, sampleRate);
}
